import React from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate, Link } from 'react-router-dom';

const Cart = () => {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();

  const incrementQuantity = (id) => {
    dispatch({
      type: 'ADD_TO_CART',
      payload: { id, quantity: 1 },
    });
  };

  const decrementQuantity = (id) => {
    dispatch({
      type: 'DECREMENT_QUANTITY',
      payload: { id },
    });
  };

  const handleRemove = (id) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      payload: { id },
    });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const handleSubmitOrder = () => {
    navigate('/shippig_payment');
  };

  const cartTotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Mon Panier</h2>
        <p className="text-slate-500 text-sm mt-1">Gérez et validez vos produits.</p>
      </div>

      {cart.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center max-w-lg mx-auto shadow-sm">
          <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <h3 className="text-lg font-semibold text-slate-700">Votre panier est vide</h3>
          <p className="text-slate-500 text-sm mt-1 mb-6">Explorez nos produits pour faire votre choix.</p>
          <Link to="/" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm px-6 py-2.5 rounded-lg transition-colors">
            Retourner au catalogue
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Liste des articles */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="hidden md:grid grid-cols-12 bg-slate-50 border-b border-slate-200 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <div className="col-span-5">Produit</div>
                <div className="col-span-2 text-center">Prix Unitaire</div>
                <div className="col-span-3 text-center">Quantité</div>
                <div className="col-span-2 text-right">Total</div>
              </div>

              <div className="divide-y divide-slate-100">
                {cart.map((item) => (
                  <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 items-center px-6 py-4 gap-4">
                    {/* Nom & Actions */}
                    <div className="col-span-12 md:col-span-5">
                      <h4 className="font-semibold text-slate-800 text-sm md:text-base">{item.name}</h4>
                      <button
                        onClick={() => handleRemove(item.id)}
                        className="text-xs text-rose-500 hover:text-rose-600 transition-colors mt-1 font-medium"
                      >
                        Retirer du panier
                      </button>
                    </div>

                    {/* Prix unitaire */}
                    <div className="col-span-6 md:col-span-2 md:text-center text-sm text-slate-600">
                      <span className="md:hidden text-xs text-slate-450 block font-medium">Prix unitaire :</span>
                      {item.price.toFixed(2)} €
                    </div>

                    {/* Quantité boutons */}
                    <div className="col-span-6 md:col-span-3 flex md:justify-center">
                      <div className="flex items-center border border-slate-200 rounded-lg bg-slate-50 overflow-hidden">
                        <button
                          onClick={() => decrementQuantity(item.id)}
                          className="px-2.5 py-1 hover:bg-slate-200 text-slate-600 font-bold transition-colors"
                        >
                          -
                        </button>
                        <span className="w-10 text-center text-sm font-semibold text-slate-700">{item.quantity}</span>
                        <button
                          onClick={() => incrementQuantity(item.id)}
                          className="px-2.5 py-1 hover:bg-slate-200 text-slate-600 font-bold transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Total item */}
                    <div className="col-span-12 md:col-span-2 text-right text-sm font-semibold text-slate-800">
                      <span className="md:hidden text-xs text-slate-450 block font-medium">Sous-total :</span>
                      {(item.price * item.quantity).toFixed(2)} €
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={clearCart}
              className="text-sm font-medium text-rose-600 hover:text-rose-700 transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Vider le panier
            </button>
          </div>

          {/* Synthèse à droite */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
            <h3 className="text-lg font-bold text-slate-800 border-b border-slate-105 pb-3">Résumé</h3>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Sous-total articles</span>
                <span>{cartTotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Frais de port</span>
                <span className="text-emerald-600 font-medium">Gratuit</span>
              </div>
              <div className="border-t border-slate-100 pt-3 flex justify-between font-bold text-base text-slate-800">
                <span>Total</span>
                <span>{cartTotal.toFixed(2)} €</span>
              </div>
            </div>

            <button
              onClick={handleSubmitOrder}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg text-sm transition-colors text-center block shadow-sm shadow-indigo-600/10"
            >
              Passer la commande
            </button>

            <Link to="/" className="w-full text-slate-400 hover:text-slate-500 font-medium text-xs text-center block py-1.5">
              Continuer mes achats
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
